// frontend/src/components/user/AssignedComplaints.js
// "My Assigned Complaints" — the logged-in user's assigned queue.
import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Grid,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  IconButton,
  Tooltip,
  TablePagination,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Divider,
  CircularProgress,
  Chip
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ROWS_PER_PAGE_OPTIONS,
  readPageParam,
  readRowsParam,
  useFilterParamSync,
  useClampedPage
} from '../../utils/urlFilters';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import api from '../../services/api';
import PriorityChip from '../common/PriorityChip';

const cellStyle = {
  maxWidth: '150px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
};

const getStatusColor = (status) => {
  switch ((status || '').toLowerCase()) {
    case 'new':
      return '#2196f3';
    case 'open':
      return '#9c27b0';
    case 'pending':
      return '#ff9800';
    case 'solved':
    case 'resolved':
      return '#4caf50';
    default:
      return '#757575';
  }
};

const STATUS_OPTIONS = ['New', 'Open', 'Pending', 'Solved', 'Resolved'];

const AssignedComplaints = () => {
  // Filters live in the URL so they survive opening a complaint and coming
  // back. See src/utils/urlFilters.js.
  const [searchParams, setSearchParams] = useSearchParams();

  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get('q') || '');
  const [statusFilter, setStatusFilter] = useState(() => searchParams.get('status') || '');
  const [page, setPage] = useState(() => readPageParam(searchParams));
  const [rowsPerPage, setRowsPerPage] = useState(() => readRowsParam(searchParams));
  const navigate = useNavigate();

  useFilterParamSync(searchParams, setSearchParams, {
    q: searchQuery,
    status: statusFilter
  }, page, rowsPerPage);

  useEffect(() => {
    const fetchAssigned = async () => {
      try {
        const res = await api.get('/complaints', { params: { assignedTo: 'me' } });
        setComplaints(Array.isArray(res.data) ? res.data : []);
        setError(null);
      } catch (err) {
        console.error('Error fetching assigned complaints:', err);
        setError('Failed to load your assigned complaints.');
      } finally {
        setLoading(false);
      }
    };
    fetchAssigned();
  }, []);

  const filteredComplaints = complaints.filter((complaint) => {
    if (statusFilter && (complaint.status || '').toLowerCase() !== statusFilter.toLowerCase()) {
      return false;
    }
    if (searchQuery) {
      const searchStr = [
        complaint.complaintId,
        complaint.name,
        complaint.subject,
        complaint.category,
        complaint.status,
        complaint.panchayath,
        complaint.mobileNumber
      ]
        .filter((val) => typeof val === 'string')
        .join(' ')
        .toLowerCase();
      if (!searchStr.includes(searchQuery.toLowerCase())) return false;
    }
    return true;
  });

  const sortedComplaints = filteredComplaints
    .slice()
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  const paginatedComplaints = sortedComplaints.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  useClampedPage(loading, sortedComplaints.length, rowsPerPage, page, setPage);

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Container maxWidth={false} sx={{ width: '100%', mt: 7, p: 2, bgcolor: 'var(--bg-color)' }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" align="left" sx={{ color: 'var(--text-color)', fontWeight: 600, mb: 2 }}>
          My Assigned Complaints
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="Search Complaints"
              variant="outlined"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(0);
              }}
              fullWidth
              size="small"
              inputProps={{ 'aria-label': 'Search Complaints' }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl variant="outlined" fullWidth size="small">
              <InputLabel id="assigned-status-filter-label">Status</InputLabel>
              <Select
                labelId="assigned-status-filter-label"
                label="Status"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="">All</MenuItem>
                {STATUS_OPTIONS.map((status) => (
                  <MenuItem key={status} value={status}>
                    {status}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress aria-label="Loading assigned complaints" />
        </Box>
      ) : error ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="error">{error}</Typography>
        </Box>
      ) : (
        <>
          <TableContainer component={Paper} sx={{ boxShadow: 3, borderRadius: 1, overflow: 'hidden' }}>
            <Table>
              <TableHead sx={{ bgcolor: 'var(--primary-color)' }}>
                <TableRow>
                  <TableCell sx={{ color: '#fff', ...cellStyle }}>Complaint ID</TableCell>
                  <TableCell sx={{ color: '#fff', ...cellStyle }}>Name</TableCell>
                  <TableCell sx={{ color: '#fff', ...cellStyle }}>Subject</TableCell>
                  <TableCell sx={{ color: '#fff', ...cellStyle }}>Category</TableCell>
                  <TableCell sx={{ color: '#fff', ...cellStyle }}>Priority</TableCell>
                  <TableCell sx={{ color: '#fff', ...cellStyle }}>Status</TableCell>
                  <TableCell sx={{ color: '#fff', ...cellStyle }}>Created</TableCell>
                  <TableCell align="center" sx={{ color: '#fff' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedComplaints.map((complaint) => (
                  <TableRow
                    key={complaint._id}
                    onClick={() => navigate(`/complaint/${complaint._id}`)}
                    sx={{
                      '&:hover': {
                        backgroundColor: 'rgba(25, 118, 210, 0.08)',
                        cursor: 'pointer',
                        transition: 'background-color 0.3s'
                      }
                    }}
                  >
                    <TableCell sx={cellStyle}>{complaint.complaintId || '–'}</TableCell>
                    <TableCell sx={cellStyle}>{complaint.name}</TableCell>
                    <TableCell sx={cellStyle}>
                      <Tooltip title={complaint.subject || ''} arrow>
                        <span>{complaint.subject}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={cellStyle}>{complaint.category || 'N/A'}</TableCell>
                    <TableCell sx={cellStyle}>
                      <PriorityChip priority={complaint.priority} />
                    </TableCell>
                    <TableCell sx={cellStyle}>
                      <Chip
                        label={complaint.status || 'Unknown'}
                        size="small"
                        sx={{
                          backgroundColor: getStatusColor(complaint.status),
                          color: 'white',
                          fontWeight: 600
                        }}
                      />
                    </TableCell>
                    <TableCell sx={cellStyle}>{formatDate(complaint.createdAt)}</TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()} align="center">
                      <Tooltip title="View Complaint">
                        <IconButton
                          color="primary"
                          onClick={() => navigate(`/complaint/${complaint._id}`)}
                          aria-label="View Complaint"
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit Complaint">
                        <IconButton
                          color="secondary"
                          onClick={() => navigate(`/complaints/edit/${complaint._id}`)}
                          aria-label="Edit Complaint"
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {sortedComplaints.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      No complaints assigned to you
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={sortedComplaints.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          />
        </>
      )}
    </Container>
  );
};

export default AssignedComplaints;
